import _ from "lodash";
import { I18N } from "oc-I18N";
import * as Commons from "oc-commons/Commons";
import * as Maths from "oc-commons/Maths";
import { Bag } from "oc-commons/Bag2";
import { v2, v3 } from "oc-threejs/short3";
import * as Scenes from "oc-threejs/Scenes";
import * as Textures from "oc-threejs/Textures";
import {
  ArrowHelper,
  Box2,
  Box3,
  BufferGeometry,
  Color,
  Group,
  Line,
  LineBasicMaterial,
  Mesh,
  MeshBasicMaterial,
  SphereGeometry,
  Vector2,
  Vector3,
} from "three";
import { Arrangement } from "./Arrangement";
import { Pfosten } from "./Pfosten";
import { ZElement } from "./ZElement";
import { Segment } from "./Segment";
import { Jaco } from "oc-jaco3d/arrangement/Jaco";
import * as Maths3 from "oc-threejs/Maths3";
import * as Maths2 from "oc-threejs/Maths2";
import * as polygonClipping from "polygon-clipping";
import Delaunator from "delaunator";
import { PairingHeap, PairingNode } from "@flemist/pairing-heap";
import { Strecke2 } from "./grundrissFunctions";
import { epsilon } from "./constants";
import ClipperLib from "js-clipper";
import rbush from "rbush";
import { OBB } from "three/examples/jsm/math/OBB";
import { getTrafoTypenVerwendbar } from "../components/zauneditor/LichtOptionen";
import { Aufbau } from "./Aufbau";

export const HINDERNIS_GEWICHT = 1;
export const SEGMENT_GEWICHT = 1;
export const RASEN_GEWICHT = 5;
export const MAX_KABELLÄNGE = 30;
export const TRAFO_KABELLÄNGE = 0.5;
export const NEW_TRAFO_OFFSET = v2(0, 0.35);
export const POLYGON_OFFSET = 0.02;
export const VERTEX_RESOLUTION = 1 / 0.02;
export const INTERSECTION_EPSILON = 0.0001;

export type VertexIdx = number;
// Trafos und Lichtleisten sind "terminale" Vertices
export type TerminalIdx = number;
export type Weight = number;
export type EdgeId = number;
export type PointId = number;
export type Polygon = Vector2[];

export interface Kabel {
  länge: number;
  pfad: Vector3[];
  lichtAnschlüsse: Vector3[];
  startAnschluss: "Trafo" | "Bus" | null;
  endAnschluss: "Bus" | null;
  // Anzahl der Schlaufen im Kabel - nur für Tests wichtig
  schlaufen: number;
}

// Jeweils pro Segment ein Array von Anschluss-Position jeder Lichtleiste | Lichtkappe
export type PfostenPosition = {
  position: Vector2;
  lichtkappe: boolean;
  lichtleiste: boolean;
  // Zur Ermittlung der unverbundenen Lichtleisten oder Lichtkappen an dieser Pfostenposition
  // Jedes Flag entspricht einer Lichtleiste oder Lichtkappe, und Lichtleisten können
  // jeweils am linken oder rechten Pfosten angeschlossen werden.
  // Wenn eine Lichtleiste an einer Pfostenposition angeschlossen wird,
  // dann ist damit implizit auch die zweite Pfostenposition des Elementes als
  // verbunden markiert
  lichtflags: LichtFlag[];
  segmente: Segment[];
  anHindernis: boolean;
};

export interface Lichtleistenabschnitte {
  pfostenPositionen: PfostenPosition[];
  pfostenPositionenEdges: [PfostenPosition, PfostenPosition][];
}

export interface VisibilityGraph {
  // die Knoten des Graphen sind Punkte in der Ebene
  // die Vertices sind so angeordnet, dass zunächst alle Trafos, dann alle Lichtleisten
  // und dann die Hindernisse kommen
  vertices: Vector2[];
  edgeMap: Map<EdgeId, VertexAdjacency>;
  adjacencyMap: Map<VertexIdx, VertexAdjacency[]>;
  trafoIdxs: Set<TerminalIdx>;
  lichtkappeIdxs: Set<TerminalIdx>;
  lichtleisteIdxs: Set<TerminalIdx>;
  // lichtkappeIdxs union lichtleisteIdxs
  lichtIdxs: Set<TerminalIdx>;
  lichtflags: Map<TerminalIdx, LichtFlag[]>;
}

enum Lichttype {
  Lichtleiste = "Lichtleiste",
  Lichtkappe = "Lichtkappe",
}

interface LichtFlag {
  type: Lichttype;
  unverbunden: boolean;
}

export interface VertexAdjacency {
  // ein ungerichteter Graph - zwei verbundene Verices teilen sich dieselbe VertexAdjacency
  neighbours: [VertexIdx, VertexIdx];
  weight: number;
  distance: number;
}

export type TerminalAdjacencyMap = Map<TerminalIdx, TerminalAdjacency[]>;

export interface TerminalAdjacency {
  // ein ungerichteter Graph - zwei verbundene Verices teilen sich dieselbe VertexAdjacency
  neighbours: [TerminalIdx, TerminalIdx];
  weight: number;
  distance: number;
  // der Pfad von neighbours[0] zu neighbours[1]
  path: Vector2[];
}

export interface TreeNode {
  id: number;
  children: TreeNode[];
  // der Pfad vom Parent-Tree-Node zu diesem Tree-Node
  // kann 1 (dann ist es der Root) oder mehr Elemente haben
  path: Vector2[];
  // die integrierte euklidische Distanz des path, ist 0 für den Root
  distance: number;
  // Ist path-End eine Lichtleiste?
  lichtleiste: boolean;
  // Ist path-End eine Lichtkappe?
  lichtkappe: boolean;
}

export interface Tree {
  root: TreeNode;
  // die Anzahl der Lichtleisten, die im Baum insgesamt vorkommen
  anzahlLichtleisten: number;
  // die Anzahl der Lichtkappen, die im Baum insgesamt vorkommen
  anzahlLichtkappen: number;
  // die Summe aller pathDistances des Tree
  distance: number;
}

function unterteileHindernisse(hindernisse: Polygon[], resolution: number) {
  return hindernisse.map((polygon) => Maths2.subdividePolygon(polygon, resolution));
}

function getRBushBoundingBox(
  p1: Vector2,
  p2: Vector2
): { minX: number; minY: number; maxX: number; maxY: number } {
  const minX = Math.min(p1.x, p2.x);
  const minY = Math.min(p1.y, p2.y);
  const maxX = Math.max(p1.x, p2.x);
  const maxY = Math.max(p1.y, p2.y);
  return { minX, minY, maxX, maxY };
}

function getSpatialIndex(polygons: Polygon[]) {
  const index = new rbush<{ polygon: Polygon }>();
  polygons.forEach((polygon) => {
    const box = getBoundingBox(polygon);
    const rbox = getRBushBoundingBox(box.min, box.max);
    index.insert({ ...rbox, polygon });
  });
  return index;
}

export interface TrafoTyp {
  Key: string;
  Leistung: number;
}

export function getTrafoTypen() {
  const { TrafoLeistung2, TrafoLeistung3, TrafoLeistung15 } = APP.settings.licht;
  return [
    { Key: "Licht_Trafo_2_Elemente", Leistung: TrafoLeistung2 },
    { Key: "Licht_Trafo_3_Elemente", Leistung: TrafoLeistung3 },
    { Key: "Licht_Trafo_15_Elemente", Leistung: TrafoLeistung15 },
  ] as TrafoTyp[];
}

export const getMaxTrafoTyp = Commons.cacheSingle(() =>
  _.maxBy(getTrafoTypen(), (t) => t.Leistung)
);

export function getTrafoTypAusgewöhlt(): TrafoTyp {
  const trafosVerwendbar = getTrafoTypenVerwendbar();
  const trafoAuswahlwertKey =
    APP.arrangement.lichttrafoAuswahlwert?.Key ?? "Licht_Trafo_automatisch";
  return trafoAuswahlwertKey === "Licht_Trafo_automatisch"
    ? _.minBy(trafosVerwendbar, (t) => t.Leistung)
    : trafosVerwendbar.find(({ Key }) => Key == trafoAuswahlwertKey);
}

export function getKabelbäumeFromArrangement(arrangement: Arrangement): Tree[] {
  // Noch eine Idee für später: Knickpunkte in Kabel - Kabel können manuell verschoben werden,
  // und zwar durch Drag auf Kabel werden neue Knickpunkte eingefügt. Bestehende Knickpunkte
  // können verschoben werden. Bestehende Ansatzpunkte (wo sie z.B. auf LichtleistenAbschnitte oder
  // Hindernisse gehen) können verschoben werden.

  const trafoPositionen = getTrafoPositionen(arrangement);
  const segmentStrecken = getSegmentStrecken(arrangement);
  let hindernisse = getHindernisPolygone(arrangement);
  let { pfostenPositionen, pfostenPositionenEdges } = getPfostenPositionen(arrangement);

  ({ pfostenPositionen, hindernisse } = behandlePfostenAnHindernissen(
    pfostenPositionen,
    hindernisse
  ));
  hindernisse = unterteileHindernisse(hindernisse, 1);

  // Wir können einfach jeden Node mit jedem verbinden - erst wenn es keine direkte Verbindung
  // gibt (wenn es ein Hindernis gibt), muss man einen Weg drum herum finden!

  const visibilityGraph = getVisibilityGraph(
    trafoPositionen,
    { pfostenPositionen, pfostenPositionenEdges },
    hindernisse
  );
  // showAdjacencyDebug(visibilityGraph, visibilityGraph.adjacencyMap, "adjacencyMap", -0.1, true);
  const terminalAdjacencyMap = getTerminalAdjacencyMap(visibilityGraph);
  // showAdjacencyDebug(visibilityGraph, terminalAdjacencyMap, "terminalAdjacencyMap", -1, false);
  const {
    vertices,
    trafoIdxs,
    lichtIdxs,
    lichtkappeIdxs,
    lichtleisteIdxs,
    edgeMap,
    adjacencyMap,
    lichtflags,
  } = visibilityGraph;
  const trees = getKabelbäume(
    terminalAdjacencyMap,
    vertices,
    trafoIdxs,
    lichtIdxs,
    lichtkappeIdxs,
    lichtleisteIdxs,
    hindernisse,
    segmentStrecken,
    lichtflags
  );
  // showTreesDebug(trees, -0.5);
  // console.log({
  //   trafoPositionen,
  //   pfostenPositionen,
  //   pfostenPositionenEdges,
  //   hindernisse,
  //   visibilityGraph,
  //   edgeMap,
  //   adjacencyMap,
  //   terminalAdjacencyMap,
  //   vertices,
  //   trafoIdxs,
  //   lichtIdxs,
  //   trees,
  // });

  return trees;
}

export function getPfostenPositionen(arrangement: Arrangement): Lichtleistenabschnitte {
  const elementeFlagMap = new Map<ZElement, LichtFlag>();
  arrangement.getElemente().forEach((element) =>
    elementeFlagMap.set(element, {
      type: Lichttype.Lichtleiste,
      unverbunden: element.hasLichtleisten,
    })
  );
  const pfostenPositionenMap = new Map<Pfosten, PfostenPosition>();

  arrangement.getPfosten().forEach((pfosten: Pfosten) => {
    const lichtkappe = pfosten.hasLichtkappe;
    const lichtleistenFlags = pfosten.elemente
      .map((element) => elementeFlagMap.get(element))
      .filter((flag) => flag?.unverbunden);
    const lichtflags = lichtkappe
      ? [{ type: Lichttype.Lichtkappe, unverbunden: true }, ...lichtleistenFlags]
      : lichtleistenFlags;
    const lichtleiste = lichtleistenFlags.length > 0;
    const worldPosition = pfosten.getWorldPosition();
    const anHindernis = pfosten.anMauer;
    pfostenPositionenMap.set(pfosten, {
      position: Maths3.v3To2xz(worldPosition),
      lichtkappe,
      lichtleiste,
      lichtflags,
      segmente: [],
      anHindernis,
    });
  });

  const pfostenPositionenEdges = [];
  arrangement.segmente.forEach((segment) => {
    const pfosten = segment.getPfosten();
    pfosten.forEach((pfosten) => {
      pfostenPositionenMap.get(pfosten).segmente.push(segment);
    });
    Commons.pairwise(pfosten).forEach((edge) => {
      pfostenPositionenEdges.push(edge.map((p) => pfostenPositionenMap.get(p)));
    });
  });
  const pfostenPositionen = [...pfostenPositionenMap.values()];
  return { pfostenPositionen, pfostenPositionenEdges };
}

export function getSegmentStrecken(arrangement: Arrangement): Strecke2[] {
  return arrangement.segmente.map(
    (segment) => segment.ecken.map((ecke) => Maths3.v3To2xz(ecke.position)) as Strecke2
  );
}

function getBoundingBox(polygon: Polygon) {
  return new Box2().setFromPoints(polygon);
}

export function offsetPolygon(polygon: Vector2[], offset: number): Vector2[] {
  const scale = 1000;
  const convertToClipperPolygon = (polygon: Vector2[]) =>
    polygon.map((point) => ({ X: point.x * scale, Y: point.y * scale }));
  const convertFromClipperPolygon = (clipperPolygon: ClipperLib.Path) =>
    clipperPolygon.map((point) => v2(point.X / scale, point.Y / scale));

  const clipperPolygon = convertToClipperPolygon(polygon);
  const clipper = new ClipperLib.ClipperOffset();
  clipper.AddPath(clipperPolygon, ClipperLib.JoinType.jtMiter, ClipperLib.EndType.etClosedPolygon);
  const solution: ClipperLib.Paths = [];
  clipper.Execute(solution, offset * scale);
  if (solution.length === 0) {
    return polygon;
  }
  return convertFromClipperPolygon(solution[0]);
}

export function behandlePfostenAnHindernissen(
  pfostenPositionen: PfostenPosition[],
  hindernisse: Polygon[]
): { pfostenPositionen: PfostenPosition[]; hindernisse: Polygon[] } {
  // prüfe jedes Hinderiss gegen jeden Pfosten. Falls der Pfosten
  // im Hindernis liegt, wird der Schnittpunkt seines Segmentes mit dem
  // Hindernis bestimmt und als neue Pfostenposition gesetzt.
  // Der Schnittpunkt wird auch als neuer Vertex zum Hindernis hinzugefügt,
  // damit die Delaunay-Triangulation den Schnittpunkt berücksichtigen kann

  const getLineFromSegment = (segment: Segment, endPoint: Vector2) => {
    // Erzeuge einen Ray entlang des Segmentes mit dem gegenüberliegenden
    // Eckpunkt als ersten Punkt und dem endpoint als zweiten
    let [e0, e1] = segment.ecken.map((e) => Maths3.v3To2xz(e.position));
    return e0.distanceTo(endPoint) > e1.distanceTo(endPoint) ? [e0, endPoint] : [e1, endPoint];
  };

  hindernisse = hindernisse.map((p: Polygon) => offsetPolygon(p, POLYGON_OFFSET));

  const hindernisBoxes: [Polygon, Box2][] = hindernisse.map((polygon: Polygon) => [
    polygon,
    getBoundingBox(polygon),
  ]);

  for (const pp of pfostenPositionen) {
    if (!pp.anHindernis) {
      continue;
    }
    const segment = pp.segmente[0];
    const line = getLineFromSegment(segment, pp.position);
    for (const [polygon, polygonBox] of hindernisBoxes) {
      if (!polygonBox.containsPoint(pp.position)) {
        continue;
      }
      const intersections = Maths2.findLinePolygonIntersections(
        line,
        polygon,
        INTERSECTION_EPSILON
      );
      if (intersections.length == 0) {
        continue;
      }
      const finalPoint = (point: Vector2) => point ?? line[1];
      const { point, segmentIndex } = _.minBy(intersections, ({ type, point, segmentIndex }) =>
        line[0].distanceTo(finalPoint(point))
      );
      if (point) {
        let p = point.clone();
        polygon.splice(segmentIndex + 1, 0, p);
        pp.position.copy(p);
      }
    }
  }
  return { pfostenPositionen, hindernisse };
}

export function unionPolygons(polygons: Polygon[]) {
  if (polygons.length < 2) {
    return polygons;
  }
  const coordPolygons = polygons.map((p) => p.map((v) => v.toArray() as polygonClipping.Pair));
  const unionCoordPolygons = polygonClipping
    .union([coordPolygons[0]], ...coordPolygons.map((a) => [a]))
    .map((u) => u[0]);
  const unionPolygons = unionCoordPolygons.map((p) => p.map(([x, y]) => v2(x, y)));
  return unionPolygons;
}

// Erzeuge jeweils Polygon-Unions aus allen zusammengesetzten Hindernis-Rechtecken.
export function getHindernisPolygone(arrangement: Arrangement): Polygon[] {
  const hindernisRechtecke: Polygon[] = arrangement.aufbauten
    .filter((a) => !a.isTrafo())
    .map((a) => {
      const points = a.getEckenV2();
      return [...points, points[0]];
    });
  return unionPolygons(hindernisRechtecke);
}

export function getTrafoPositionen(arrangement: Arrangement) {
  return arrangement.aufbauten
    .filter((a) => a.isTrafo())
    .map((a) => Maths3.v3To2xz(a.getWorldPosition()));
}

export function createUndirectedAdjacencyGraph<
  VertIdx extends VertexIdx,
  Adjacency extends {
    neighbours: [VertIdx, VertIdx];
    weight: number;
  },
>() {
  const edgeMap = new Map<EdgeId, Adjacency>();
  const adjacencyMap = new Map<VertIdx, Adjacency[]>();
  const ensureAdjacencyForVertex = (start: VertIdx): Adjacency[] => {
    let list = adjacencyMap.get(start);
    if (list == null) {
      list = [];
      adjacencyMap.set(start, list);
    }
    return list;
  };
  const pushEdge = (
    vertexIdx0: VertIdx,
    vertexIdx1: VertIdx,
    partialAdjacency: Omit<Adjacency, "neighbours">
  ) => {
    const edgeId = getEdgeIde(vertexIdx0, vertexIdx1);
    const adjacency = {
      ...partialAdjacency,
      neighbours: sortVertices(vertexIdx0, vertexIdx1) as [VertIdx, VertIdx],
    } as Adjacency;
    edgeMap.set(edgeId, adjacency);
    [vertexIdx0, vertexIdx1].forEach((v) => ensureAdjacencyForVertex(v).push(adjacency));
  };
  return { edgeMap, adjacencyMap, ensureAdjacencyForVertex, pushEdge };
}

/**
 * Erzeuge den Sichtbarkeitsgraphen:
 * Wende die Delaunay-Triangulation auf die Eckpunkte der LichtleistenAbschnitte, den Trafo-Positionen und
 * der Hindernis-Polygone an. Das Ergebnis ist ein ungerichteter Graph, in dem:
 * - keine Kanten innerhalb von Hindernissen existieren
 * - vollständig alle Kanten auf Hindernissen und Zaunlinien existieren
 * - Kanten auf Hindernissen und Zaunlinien und solche durch den Garten unterschiedlich gewichtet sind
 * Warum reicht die Delaunay-Triangulation? Für einen Euklidischen Graph ist der MST Teilgraph der
 * Delaunay-Triangulation.
 * Wir haben zwar einen gewichteten Graphen, allerdings haben wir nur zwei Klassen von Gewichtung:
 * 1. Ränder (Zaunsegment, Hindernispolygon) mit einer sehr starken Gewichtung von 5.
 * 2. Freie Flöchen (Rasen) mit einer schwachen Gewichtung von 1.
 * Die wichtigste Verbindung ist der Trafo zum Zaunsegment - das ist perfekt abgedeckt in der Triangulation.
 * Alle weiteren Verbindungen laufen sonst innerhalb der Segmente oder um Hindernisse herum, oder auf möglichst
 * kurzer gerader Verbindung über freie Flächen. Letzteres ist bei der Triangulation meistens ok, nur in
 * einigen Grenzfällen sind unglückliche Umwege denkbar, wenn wirklich komplett quer durch das Grundstück
 * gelegt würde und die Triangulation zufällig nicht passt.
 */
export function getVisibilityGraph(
  trafoPositionen: Vector2[],
  { pfostenPositionen, pfostenPositionenEdges }: Lichtleistenabschnitte,
  hindernisse: Polygon[]
): VisibilityGraph {
  const coords: number[] = [];
  const vertices: Vector2[] = [];
  const lichtflags = new Map<TerminalIdx, LichtFlag[]>();

  const verticesMap = new Map<PointId, VertexIdx>();
  function registerVertexIdx(vertex: Vector2) {
    const pid = getPointId(vertex);
    let vid = verticesMap.get(pid);
    if (vid == null) {
      vid = vertices.length;
      verticesMap.set(pid, vid);
      vertices.push(vertex);
      coords.push(vertex.x, vertex.y);
    }
    return vid;
  }

  const vertexToObjects = new Map<TerminalIdx, any[]>();
  function registerObject(vid: number, object: any[] | any) {
    let objects = vertexToObjects.get(vid);
    if (objects == null) {
      objects = [];
      vertexToObjects.set(vid, objects);
    }
    if (Array.isArray(object)) {
      objects.push(...object);
    } else {
      objects.push(object);
    }
    return vid;
  }

  const registerVertex = (vertex: Vector2, object: any): VertexIdx =>
    registerObject(registerVertexIdx(vertex), object);

  const {
    edgeMap,
    adjacencyMap,
    pushEdge: pushEdge_,
  } = createUndirectedAdjacencyGraph<VertexIdx, VertexAdjacency>();

  const pushEdge = (vertexIdx0: VertexIdx, vertexIdx1: VertexIdx, weight_: number) => {
    const v0 = vertices[vertexIdx0];
    const v1 = vertices[vertexIdx1];
    const distance = v0.distanceTo(v1);
    const weight = distance * weight_;
    pushEdge_(vertexIdx0, vertexIdx1, { weight, distance });
  };

  const addObject = (object: any, vertices_: Polygon, asEdgeWithWeight: number): VertexIdx[] => {
    const vidxs = vertices_.map((v) => registerVertex(v, object));
    if (asEdgeWithWeight != null) {
      for (let i = 1; i < vidxs.length; i++) {
        pushEdge(vidxs[i - 1], vidxs[i], asEdgeWithWeight);
      }
    }
    return vidxs;
  };

  const trafoIdxs = new Set<TerminalIdx>();

  // die Reihenfolge ist eine Konvention: Trafos sind die ersten Vertixes, dann die LichtleistenAbschnitte, dann die Hindernisse
  trafoPositionen.forEach((position) => {
    addObject(position, [position], null).forEach((vidx) => trafoIdxs.add(vidx));
  });

  const lichtIdxs = new Set<TerminalIdx>();
  const lichtkappeIdxs = new Set<TerminalIdx>();
  const lichtleisteIdxs = new Set<TerminalIdx>();

  const pfostenPositionVertexMap = new Map<PfostenPosition, TerminalIdx>();
  pfostenPositionen.forEach((pfostenPosition) => {
    const position = pfostenPosition.position;
    const idx = addObject(pfostenPosition.segmente, [position], null)[0];
    pfostenPositionVertexMap.set(pfostenPosition, idx);
    if (pfostenPosition.lichtkappe || pfostenPosition.lichtleiste) {
      lichtIdxs.add(idx);
      lichtflags.set(idx, pfostenPosition.lichtflags);
      if (pfostenPosition.lichtkappe) {
        lichtkappeIdxs.add(idx);
      }
      if (pfostenPosition.lichtleiste) {
        lichtleisteIdxs.add(idx);
      }
    }
  });

  pfostenPositionenEdges.forEach((edge: [PfostenPosition, PfostenPosition]) => {
    const [i, j] = edge.map((a) => pfostenPositionVertexMap.get(a));
    pushEdge(i, j, SEGMENT_GEWICHT);
  });

  hindernisse.forEach((polygon) => addObject(polygon, polygon, HINDERNIS_GEWICHT));

  const isSameObject = (a: VertexIdx, b: VertexIdx) => {
    const as = vertexToObjects.get(a);
    const bs = vertexToObjects.get(b);
    return as != null && bs != null && as.some((a) => bs.some((b) => a == b));
  };

  const helper = new DelaunatorHelper(new Delaunator(coords));
  // showDelauniatorDebug(helper, 0);
  const hindernisseQuery = getSpatialIndex(hindernisse);
  edgeLoop: for (const [a, b] of helper.iterateEdgePoints()) {
    // Ignoriere Kanten, die in Hindernissen liegen oder auf demselben Segment
    // kurz, alle Vertixes, die Teil desselben Objektes sind
    if (edgeMap.has(getEdgeIde(a, b)) || isSameObject(a, b)) {
      continue edgeLoop;
    }

    // prüft noch einmal explizit auf Schnitt der Kante mit einem Hindernis - leider reicht
    // isSameObject nicht für alle Fälle
    const strecke = [vertices[a], vertices[b]];
    const edgeBbox = getRBushBoundingBox(strecke[0], strecke[1]);
    const queryResult = hindernisseQuery.search(edgeBbox);
    for (const { polygon } of queryResult) {
      if (strecke.every((point) => Maths2.isPointInPolygon(point, polygon))) {
        continue edgeLoop;
      }
      const intersections = Maths2.findLinePolygonIntersections(strecke, polygon);
      if (intersections.some(({ type }) => type === "X")) {
        continue edgeLoop;
      }
    }
    pushEdge(a, b, RASEN_GEWICHT);
  }

  return {
    vertices,
    edgeMap,
    adjacencyMap,
    trafoIdxs,
    lichtIdxs,
    lichtkappeIdxs,
    lichtleisteIdxs,
    lichtflags,
  };
}

/**
 * Löse für jeden terminalen Knoten das single-source shortest path problem mittels Dijkstra
 * und bilde daraus den terminalen Graphen: ~ O(n² log n)
 */
export function getTerminalAdjacencyMap(graph: VisibilityGraph): TerminalAdjacencyMap {
  const { vertices, adjacencyMap, trafoIdxs, lichtIdxs } = graph;
  const verticesCount = vertices.length;

  const { adjacencyMap: terminalAdjacencyMap, pushEdge } = createUndirectedAdjacencyGraph<
    TerminalIdx,
    TerminalAdjacency
  >();

  const terminalIdxs = [...trafoIdxs, ...lichtIdxs];
  const terminalCount = terminalIdxs.length;

  // Berechne Dijkstra für jeden terminalen Knoten, d.h. all-source shortest path problem
  for (let i_ = 0; i_ < terminalCount - 1; i_++) {
    const i = terminalIdxs[i_];
    const { weights, distances, previous } = dijkstra(verticesCount, adjacencyMap, i);
    for (let j_ = i_ + 1; j_ < terminalCount; j_++) {
      const j = terminalIdxs[j_];
      const weight = weights[j];
      if (weight < Infinity) {
        pushEdge(i, j, {
          weight: weights[j],
          distance: distances[j],
          path: getPath(j, previous).map((vertexIdx) => vertices[vertexIdx]),
        });
      }
    }
  }
  return terminalAdjacencyMap;
}

export function getVerbrauch(tree: Tree) {
  const { LichtkappeVerbrauch, LichtleisteVerbrauch } = APP.settings.licht;
  return (
    tree.anzahlLichtkappen * LichtkappeVerbrauch + tree.anzahlLichtleisten * LichtleisteVerbrauch
  );
}

/**
 * Wende den Prim-Algorithmus auf die terminalen Graphen an: ~ O(n log n)
 *   a) Initialisiere einen Baum mit dem ersten Trafo als Knoten.
 *     a.1)  Falls es keinen Trafo gibt, erzeuge einen neuen. Wähle dazu einen beliebigen terminalen
 *           Knoten aus dem Graphen. Der Trafo wird an diese Position + ein festes Delta (2,2) gesetzt
 *           und als neue Kante zum gewählten Knoten eingefügt.
 *   b)  Erweitere den Baum um eine Kante: Unter den Kanten, die den Baum mit terminalen Knoten verbinden,
 *       die noch nicht im Baum sind, finde die Kante mit dem minimalen Gewicht und füge sie dem Baum hinzu.
 *     b.1)  Falls bis der Baum dabei ungültig wird (d.h. die Anzahl Lichtleisten pro Trafo > 15):
 *           Erzeuge statt dessen einen neuen Baum und gehe zu Schritt a).
 *   c) Wiederhole Schritt b), bis alle Knoten im Baum enthalten sind
 */
export function getKabelbäume(
  adjacencyMap: TerminalAdjacencyMap,
  vertices: Vector2[],
  trafosTerminalIdx: Set<TerminalIdx>,
  lichtTerminalIdx: Set<TerminalIdx>,
  lichtkappeTerminalIdxs: Set<TerminalIdx>,
  lichtleisteTerminalIdxs: Set<TerminalIdx>,
  hindernisse: Polygon[],
  segmentStrecken: Strecke2[],
  lichtflags: Map<TerminalIdx, LichtFlag[]>
): Tree[] {
  const markiereEinLichtAlsVerbunden = (terminalIdx: TerminalIdx) => {
    const flags = lichtflags.get(terminalIdx);
    if (flags) {
      flags.forEach((flag) => {
        if (flag) {
          flag.unverbunden = false;
        }
      });
    }
  };
  const istUnverbunden = (terminalIdx: TerminalIdx) => {
    const flags = lichtflags.get(terminalIdx);
    return flags == null ? /*trafo*/ true : flags.some((flag) => flag.unverbunden);
  };

  const isValid = (tree: Tree, end: TerminalIdx, edge: TerminalAdjacency) => {
    const { LichtkappeVerbrauch, LichtleisteVerbrauch } = APP.settings.licht;
    const nextPower =
      getVerbrauch(tree) +
      // add one more light element to check if it exceeds the limit
      Math.max(LichtkappeVerbrauch, LichtleisteVerbrauch);
    return nextPower <= getMaxTrafoTyp().Leistung;
  };

  let idCounter = 0;

  const createTreeNode = (end: TerminalIdx, minEdge: TerminalAdjacency): TreeNode => {
    let path = minEdge?.path ?? [];
    if (minEdge != null && minEdge.neighbours[0] === end) {
      path = path.toReversed();
    }
    return {
      id: idCounter++,
      children: [],
      path: path,
      distance: minEdge?.distance ?? 0,
      lichtleiste: lichtleisteTerminalIdxs.has(end),
      lichtkappe: lichtkappeTerminalIdxs.has(end),
    };
  };

  const zähleLichter = (flags: LichtFlag[]) => {
    let anzahlLichtleisten = 0;
    let anzahlLichtkappen = 0;
    flags.forEach((flag) => {
      if (flag.type == Lichttype.Lichtleiste) {
        anzahlLichtleisten = 1;
      } else if (flag.type == Lichttype.Lichtkappe) {
        anzahlLichtkappen = 1;
      }
    });
    return { anzahlLichtkappen, anzahlLichtleisten };
  };

  const addTreeNode = (
    tree: Tree,
    start: TerminalIdx,
    end: TerminalIdx,
    minEdge: TerminalAdjacency
  ) => {
    const node = createTreeNode(end, minEdge);
    // console.log(`  treeMap.set(${end}, node)`);
    treeMap.set(end, node);
    // console.log(`  treeMap.get(${start}).children`);
    treeMap.get(start).children.push(node);
    const unverbunden = istUnverbunden(end);
    if (unverbunden) {
      markiereEinLichtAlsVerbunden(end);
      const { anzahlLichtkappen, anzahlLichtleisten } = zähleLichter(lichtflags.get(end));
      tree.anzahlLichtkappen += anzahlLichtkappen;
      tree.anzahlLichtleisten += anzahlLichtleisten;
    }
    tree.distance += node.distance;
  };

  interface TreeContext {
    mst: () => Generator<{ start: TerminalIdx; minEdge: TerminalAdjacency }>;
    tree: Tree;
    id: number;
  }

  let treeid = 0;

  const createTreeContext = (trafoIdx: number): TreeContext => {
    const root = createTreeNode(null, null);
    treeMap.set(trafoIdx, root);
    // console.log(`  treeMap.set(${trafoIdx}, root)`);
    const tree = {
      root,
      anzahlLichtleisten: 0,
      anzahlLichtkappen: 0,
      distance: 0,
    };
    const { reset, mst } = prim<TerminalIdx, TerminalAdjacency>(
      adjacencyMap,
      visited,
      istUnverbunden
    );
    reset(trafoIdx);
    return {
      mst,
      tree,
      id: treeid++,
    };
  };

  const createTrafo = () => {
    const { trafoPosition, lichtIdx } = findTrafoPosition(
      vertices,
      lichtTerminalIdx,
      hindernisse,
      segmentStrecken,
      istUnverbunden
    );
    if (!trafoPosition) {
      return null;
    }
    const trafoIdx = vertices.length;
    const lichtPosition = vertices[lichtIdx];
    // console.log("### trafoPosition", { trafoPosition });
    vertices.push(trafoPosition);
    trafosTerminalIdx.add(trafoIdx);
    const distance = trafoPosition.distanceTo(lichtPosition);
    const edge: TerminalAdjacency = {
      neighbours: [trafoIdx, lichtIdx],
      weight: distance * RASEN_GEWICHT,
      distance,
      path: [trafoPosition, lichtPosition],
    };
    adjacencyMap.set(trafoIdx, [edge]);
    return trafoIdx;
  };

  function fügeZwischenknotenFürGemeinsamenAnfangspfadEin(node: TreeNode): TreeNode {
    const paths = node.children.map((p) => p.path);
    const path = Commons.getFirstCommonPart(paths, (a, b) => a.equals(b));
    if (path.length < 2) {
      return;
    }
    const l = path.length - 1;
    const children = node.children.map((c, i) => {
      const path = paths[i].slice(l);
      const distance = Maths2.sumPathDistance(c.path);
      return {
        ...c,
        path,
        distance,
      };
    });
    node.children = [
      {
        id: idCounter++,
        children,
        path,
        distance: Maths2.sumPathDistance(path),
        lichtkappe: false,
        lichtleiste: false,
      },
    ];
  }

  function correctRootNode(tree: Tree) {
    let root = tree.root;
    if (root.children.length > 0) {
      const firstNode = root.children.length == 1 ? root.children[0] : root;
      // Der firstNode sollte vom Trafo zu nur einer child (Lichtleiste) gehen.
      // Trotzdem kann dieser Fall vorkommen mit mehr als einem child geben, nämlich
      // wenn eine Lichtliste hinter einem Trafo bereits bei einem
      // anderen Trafo angeschlossen ist. Wir müssen für die Kabelverlegung aber sicherstellen,
      // dass das Kabel nicht über den Trafo zurückläuft.
      // Dafür müssen wir sicherstellen, das firstNode nur ein Child hat.
      if (firstNode.children.length > 1) {
        fügeZwischenknotenFürGemeinsamenAnfangspfadEin(firstNode);
      }
    }
    return tree;
  }

  // console.log("## start prim ##");

  const visited = new Set<TerminalIdx>();
  const treeMap = new Map<TerminalIdx, TreeNode>();

  const createTrafoAndTreeContext = () => {
    const trafoIdx = createTrafo();
    if (trafoIdx == null) {
      return null;
    }
    const context = createTreeContext(trafoIdx);
    allTreeContexts.add(context);
    return context;
  };
  const allTreeContexts = trafosTerminalIdx.map(createTreeContext);
  const activeTreeContexts = [...allTreeContexts];
  const getNextTreeContext = () => {
    let context = activeTreeContexts.shift();
    if (context == null) {
      context = createTrafoAndTreeContext();
      if (context == null) {
        return null;
      }
      allTreeContexts.add(context);
    }
    return context;
  };

  let context = getNextTreeContext();
  while (context) {
    const { tree, mst } = context;
    const { done, value } = mst().next();
    if (done) {
      break;
    }
    if (isValid(tree, value.minEnd, value.minEdge)) {
      if (istUnverbunden(value.minEnd)) {
        if (!treeMap.has(value.start)) {
          console.error(`node ${value.start} is missing!`);
          break;
        } else {
          addTreeNode(tree, value.start, value.minEnd, value.minEdge);
        }
      } else {
        console.error(`node ${value.minEnd} is verbunden!`);
      }
    } else {
      visited.delete(value.minEnd);
      context = getNextTreeContext();
    }
  }

  const trees = [...allTreeContexts]
    .filter(({ tree }) => tree.anzahlLichtleisten + tree.anzahlLichtkappen > 0)
    .map(({ tree }) => correctRootNode(tree));

  return trees;
}

/**
 * stellt sicher, dass der neue Trafo nahe an einer freien Lichtleiste steht und nicht innerhalb
 * eines Hindernisses liegt. Ist aber nicht perfekt:
 * 1. die Position kann auf einem Hindernis liegen
 * 2. die direkte Strecke zur Lichtleiste kann durch ein Hindernis gehen
 * Das kann der Benutzer aber erkennen und den Trafo selbst platzieren. Wichtig ist vor allem,
 * das der Trafo nicht innerhalb eines Hindernisses liegt, weil man ihn dann nicht sieht.
 */
function findTrafoPosition(
  vertices: Vector2[],
  lichtTerminalIdx: Set<TerminalIdx>,
  hindernisse: Polygon[],
  segmentStrecken: Strecke2[],
  istUnverbunden: (terminalIdx: TerminalIdx) => boolean
) {
  const lichtIdxs = lichtTerminalIdx.filter(istUnverbunden);
  for (const lichtIdx of lichtIdxs) {
    const lichtleistePosition = vertices[lichtIdx];
    for (let r = 0; r < 360; r += 45) {
      const trafoPosition = lichtleistePosition
        .clone()
        .add(NEW_TRAFO_OFFSET)
        .rotateAround(lichtleistePosition, (r * Math.PI) / 180);
      if (
        hindernisse.some((polygon) => Maths2.isPointInPolygon(trafoPosition, polygon)) ||
        segmentStrecken.some(
          (strecke) => Maths2.distanceToLineSegment(trafoPosition, strecke) < 0.3
        )
      ) {
        continue;
      }
      return { trafoPosition, lichtIdx };
    }
  }
  return { trafoPosition: null, lichtIdx: null };
}

/**
 * Generates a minimum spanning tree (MST) of a graph given by an adjacency map in O(|E|+|V| log |V|).
 * Standard implementation of Prim's Algorithm with a Heap.
 *
 * Returns two functions:
 * - mst(): creates a generator that iterates over the edges of the minimum spanning tree.
 * - reset(start: Vertex): starts a new minimum spanning tree.
 *
 * Outline:
 * 1. Initialize a tree with a single start vertex
 * 2. Grow the tree by one edge: Of the edges that connect the tree to vertices not yet in the tree,
 *    find the minimum-weight edge, and transfer it to the tree.
 * 3. Repeat step 2 (until all vertices are in the tree).
 *
 * @see https://en.wikipedia.org/wiki/Prim%27s_algorithm
 */
export function prim<
  Vertex extends VertexIdx,
  Adjacency extends {
    neighbours: [Vertex, Vertex];
    weight: number;
  },
>(
  adjacencyMap: Map<Vertex, Adjacency[]>,
  visited: Set<Vertex>,
  isValid: (v: Vertex) => boolean
): {
  reset: (vertex: Vertex) => void;
  mst: () => Generator<{ start: Vertex; minEdge: Adjacency }>;
} {
  const heap = createHeap<Adjacency>();
  function addVertex(v: Vertex) {
    visited.add(v);
    const edges = adjacencyMap.get(v);
    if (edges != null) {
      for (const edge of edges) {
        heap.setOrDecrease(edge.weight, edge);
      }
    }
  }
  function reset(start: Vertex) {
    addVertex(start);
  }
  function* mst() {
    loop: while (true) {
      const minEdge = heap.deleteMin();
      if (minEdge == null) {
        break loop;
      }
      const neighbours = minEdge.neighbours;
      for (const minEnd of neighbours) {
        if (!visited.has(minEnd) && isValid(minEnd)) {
          addVertex(minEnd);
          const start = getNeighbour<Vertex>(neighbours, minEnd);
          // console.log(`yield { ${start}, ${minEnd} }`, "visited", visited.toArray().join(", "));
          yield { start, minEnd, minEdge };
          continue loop;
        }
      }
    }
  }

  return { reset, mst };
}

/**
 * löst das single-source shortest path problem, also von jeweils einem Knoten hin zu allen anderen
 * ~ O(n log n)
 */
export function dijkstra(
  verticesCount: number,
  adjacencyMap: Map<VertexIdx, VertexAdjacency[]>,
  startVertex: VertexIdx
): { weights: number[]; distances: number[]; previous: VertexIdx[] } {
  const distances: VertexIdx[] = new Array(verticesCount).fill(0);
  const weights: VertexIdx[] = new Array(verticesCount).fill(Infinity);
  const previous: VertexIdx[] = new Array(verticesCount).fill(-1);
  weights[startVertex] = 0;

  const heap = createHeap<VertexIdx>();

  for (let i = 0; i < verticesCount; i++) {
    heap.add(weights[i], i);
  }

  while (!heap.isEmpty()) {
    const u = heap.deleteMin();
    if (adjacencyMap.has(u)) {
      for (const { neighbours, weight, distance } of adjacencyMap.get(u)) {
        const v = getNeighbour<VertexIdx>(neighbours, u);
        if (weights[u] + weight < weights[v]) {
          weights[v] = weights[u] + weight;
          distances[v] = distances[u] + distance;
          previous[v] = u;
          heap.setOrDecrease(weights[v], v);
        }
      }
    }
  }

  return { weights, distances, previous };
}

/**
 * gibt einen Dijkstra-Ergbnis-Pfad zurück
 */
export function getPath(end: VertexIdx, previous: VertexIdx[]): VertexIdx[] {
  const path = [];
  for (let at = end; at !== -1; at = previous[at]) {
    path.push(at);
  }
  path.reverse();
  return path;
}

/**
 * ein Minimum Pairing Heap.
 * A Pairing heap is in practise faster and more lightweight compared to a Fibbonacci Heap.
 */
function createHeap<Vertex>() {
  interface Node {
    weight: number;
    vertex: Vertex;
  }
  const pairingHeap = new PairingHeap<Node>({
    lessThanFunc: (a: Node, b: Node) => a.weight < b.weight,
  });
  const nodeMap = new Map<Vertex, PairingNode<Node>>();
  return {
    isEmpty: () => pairingHeap.isEmpty,
    deleteMin: (): Vertex => {
      const vertex = pairingHeap.deleteMin()?.vertex;
      if (vertex != null) {
        nodeMap.delete(vertex);
      }
      return vertex;
    },
    setOrDecrease(weight: number, vertex: Vertex) {
      const node = nodeMap.get(vertex);
      if (node) {
        if (weight < node.item.weight) {
          pairingHeap.delete(node);
          nodeMap.set(vertex, pairingHeap.add({ weight, vertex }));
          return true;
        }
      } else {
        nodeMap.set(vertex, pairingHeap.add({ weight, vertex }));
        return true;
      }
      return false;
    },
    add(weight: number, vertex: Vertex) {
      nodeMap.set(vertex, pairingHeap.add({ weight, vertex }));
    },
  };
}

/**
 * see https://mapbox.github.io/delaunator/
 */
export class DelaunatorHelper {
  delaunator: Delaunator;

  constructor(delaunator: Delaunator) {
    this.delaunator = delaunator;
  }

  startPointOfHalfEdge(e: number): [number, number] {
    const { triangles, coords } = this.delaunator;
    const t = triangles[e] * 2;
    return [coords[t], coords[t + 1]];
  }

  oppositeHalfEdgeInAdjacentTriangle(e: number): number {
    return this.delaunator.halfedges[e];
  }

  edgesOfTriangle(t: number): [number, number, number] {
    return [3 * t, 3 * t + 1, 3 * t + 2];
  }

  pointsOfTriangle(t) {
    return this.edgesOfTriangle(t).map((e) => this.delaunator.triangles[e]);
  }

  triangleOfEdge(e: number): number {
    return Math.floor(e / 3);
  }

  nextHalfEdge(e: number): number {
    return e % 3 == 2 ? e - 2 : e + 1;
  }

  prevHalfEdge(e: number): number {
    return e % 3 == 0 ? e + 2 : e - 1;
  }

  *iterateEdges() {
    const { triangles, halfedges } = this.delaunator;
    for (let e = 0; e < triangles.length; e++) {
      if (e > halfedges[e]) {
        yield e;
      }
    }
  }

  *iterateEdgePoints() {
    const { triangles, halfedges } = this.delaunator;
    for (const e1 of this.iterateEdges()) {
      const e2 = this.nextHalfEdge(e1);
      const existingPointsIdx: [number, number] = [triangles[e1], triangles[e2]];
      yield existingPointsIdx;
    }
  }

  // Problem: sind dann auch Strecken, die teilweise durch Hindernisse gehen!
  // *iterateEnhancedDelaunayEdges() {
  //   const { triangles } = this.delaunator;
  //   const edgeV2 = (es: number[]): Vector2[] => es.map((e) => v2(...this.startPointOfHalfEdge(e)));
  //   // Iteriere über alle benachbarten Dreieckspaare und füge bei Bedarf eine weitere Mittelkante hinzu
  //   // entsprechend der Sichtbarkeitsregel.
  //   for (const e1 of this.iterateEdges()) {
  //     const e2 = this.nextHalfEdge(e1);
  //     const existingPointsIdx: [number, number] = [triangles[e1], triangles[e2]];
  //     yield existingPointsIdx;
  //     const ae1 = this.oppositeHalfEdgeInAdjacentTriangle(e1);
  //     if (ae1 === -1) {
  //       continue;
  //     }
  //     const e3 = this.nextHalfEdge(e2);
  //     const ae2 = this.nextHalfEdge(ae1);
  //     const ae3 = this.nextHalfEdge(ae2);
  //     const existingEdge = edgeV2([e1, e2]);
  //     const newEdge = edgeV2([e3, ae3]);
  //     if (Maths2.segmentIntersection(existingEdge, newEdge)?.type === "X") {
  //       const newPointsIdx: [number, number] = [triangles[e3], triangles[ae3]];
  //       yield newPointsIdx;
  //     }
  //   }
  // }
}

function showAdjacencyDebug(
  visibilityGraph: VisibilityGraph,
  adjacencyMap: Map<VertexIdx, VertexAdjacency[]>,
  name: string,
  yOffset: number,
  labels: boolean
) {
  if (APP.arrangement.jaco3d == null) {
    return;
  }

  const { vertices, trafoIdxs, lichtIdxs } = visibilityGraph;

  Scenes.remove(APP.arrangement.jaco3d[name]);
  const g3 = (APP.arrangement.jaco3d[name] = new Group());
  g3.position.setY(yOffset);
  APP.arrangement.jaco3d.add(g3);
  const sphereGeometry = new SphereGeometry(0.001, 32, 32);
  const trafoMaterial = new MeshBasicMaterial({ color: 0xff0000 });
  const lichtleistenMaterial = new MeshBasicMaterial({ color: 0xffff00 });
  const obstacleMaterial = new MeshBasicMaterial({ color: 0x0000ff });

  if (labels) {
    for (let i = 0; i < vertices.length; i++) {
      const vertex = Maths3.v2To3xz(vertices[i]);
      let material;

      if (trafoIdxs.has(i)) {
        material = trafoMaterial;
      } else if (lichtIdxs.has(i)) {
        material = lichtleistenMaterial;
      } else {
        material = obstacleMaterial;
      }

      const sphere = new Mesh(sphereGeometry, material);
      sphere.position.copy(vertex);
      g3.add(sphere);
      const label = Textures.createTextSprite("" + i, { orginalHeight: 0.02 });
      label.position.setY(0.2);
      sphere.add(label);
    }
  }

  const weights = [...adjacencyMap.values()].flat().map((a: VertexAdjacency) => a.weight);
  const maxWeight = _.max(weights);
  const minWeight = _.min(weights);

  for (const [startIdx, adjacencies] of adjacencyMap.entries()) {
    const startVertex = Maths3.v2To3xz(vertices[startIdx]);

    adjacencies.forEach((adjacency) => {
      const endVertex = Maths3.v2To3xz(vertices[adjacency.neighbours[0]]);

      let material;
      if (labels) {
        const gewicht = adjacency.weight / adjacency.distance;
        const h = gewicht == SEGMENT_GEWICHT ? 0 : gewicht == RASEN_GEWICHT ? 100 : 245;
        material = new LineBasicMaterial({
          color: new Color().setHSL(h / 360, 0.5, 0.5),
        });
      } else {
        material = new LineBasicMaterial({
          color: new Color().setHSL(
            0,
            0,
            Maths.mapToUnitInterval(minWeight, adjacency.weight, maxWeight)
          ),
        });
      }

      const points = [];
      points.push(startVertex);
      points.push(endVertex);

      const geometry = new BufferGeometry().setFromPoints(points);
      const line = new Line(geometry, material);
      g3.add(line);
    });
  }
}

function showDelauniatorDebug(helper: DelaunatorHelper, yOffset: number) {
  if (APP.arrangement.jaco3d == null) {
    return;
  }

  Scenes.remove((APP.arrangement.jaco3d as any).delauniator3d);
  const g3 = ((APP.arrangement.jaco3d as any).delauniator3d = new Group());
  g3.position.setY(yOffset);
  APP.arrangement.jaco3d.add(g3);
  const sphereGeometry = new SphereGeometry(0.001, 32, 32);
  const color = 0xa0a0a0;
  const vertexMaterial = new MeshBasicMaterial({ color });
  const lineMaterial = new LineBasicMaterial({
    color,
  });

  const { coords, triangles, halfedges } = helper.delaunator;

  const toVertex = (i) => Maths3.v2To3xz(v2(coords[i * 2], coords[i * 2 + 1]));

  for (let i = 0; i < coords.length / 2; i++) {
    const sphere = new Mesh(sphereGeometry, vertexMaterial);
    sphere.position.copy(toVertex(i));
    g3.add(sphere);
  }

  const line = (points: Vector3[]) => {
    const geometry = new BufferGeometry().setFromPoints(points);
    g3.add(new Line(geometry, lineMaterial));
  };

  for (let e = 0; e < triangles.length; e++) {
    if (e > halfedges[e]) {
      const p = toVertex(triangles[e]);
      const q = toVertex(triangles[helper.nextHalfEdge(e)]);
      line([p, q]);
    }
  }
}

function showTreesDebug(trees: Tree[], yOffset: number) {
  if (APP.arrangement.jaco3d == null) {
    return;
  }

  Scenes.remove((APP.arrangement.jaco3d as any).tree3d);
  const g3 = ((APP.arrangement.jaco3d as any).tree3d = new Group());
  g3.position.setY(yOffset);
  APP.arrangement.jaco3d.add(g3);
  const sphereGeometry = new SphereGeometry(0.03, 8, 8);
  const color = 0xff0000;
  const vertexMaterial = new MeshBasicMaterial({ color });
  const lineMaterial = new LineBasicMaterial({
    color,
  });

  const point = (v: Vector3) => {
    const sphere = new Mesh(sphereGeometry, vertexMaterial);
    sphere.position.copy(v);
    g3.add(sphere);
  };
  const line = (s: Vector3[]) => {
    if (s[0].distanceTo(s[1]) < 0.1) {
      return;
    }
    const arrow = new ArrowHelper(
      s[1].clone().sub(s[0]).normalize(),
      s[0],
      s[0].distanceTo(s[1]),
      color,
      0.1,
      0.05
    );
    g3.add(arrow);
  };

  let dy = 0;
  for (const tree of trees) {
    dy -= 0.1;
    const p2 = (v: Vector2) => Maths3.v2To3xz(v, dy);
    const nodes = flattenTree(tree);
    point(p2(nodes.at(0).path.at(0)));
    for (const node of nodes) {
      Commons.pairwise(node.path.map(p2)).forEach(line);
    }
  }
}

function getPointId(point: Vector2) {
  return Maths.pairing(
    Math.floor(point.x * VERTEX_RESOLUTION),
    Math.floor(point.y * VERTEX_RESOLUTION)
  );
}

export const sortVertices = (a: VertexIdx, b: VertexIdx): [number, number] =>
  a < b ? [a, b] : [b, a];

export const getEdgeIde = (a: VertexIdx, b: VertexIdx): EdgeId =>
  a < b ? Maths.pairing(a, b) : Maths.pairing(b, a);

export const getNeighbour = <VertexIdx>(neighbours: [VertexIdx, VertexIdx], u: VertexIdx) =>
  neighbours[0] === u ? neighbours[1] : neighbours[0];

export function* iterateLichtleistenWarnungen(
  arrangement: Arrangement,
  getBoxWorld: (jaco: Jaco) => OBB
) {
  const trafos = arrangement.aufbauten.filter((a) => a.isTrafo());
  const trafoConnectionEpsilon = 0.08;

  const isTrafoMitKabelVerbunden = (trafo: Aufbau) => {
    const trafoPos2 = Maths3.v3To2xz(trafo.getWorldPosition());
    const kabelVonTrafo = arrangement.kabel.filter(
      (kabel) => kabel.trafo === trafo && kabel.startAnschluss === "Trafo"
    );
    return kabelVonTrafo.some((kabel) => {
      const cableEndpoints = [kabel.pfad.at(0), kabel.pfad.at(-1)].filter(Boolean);
      return cableEndpoints.some(
        (p3) => Maths3.v3To2xz(p3).distanceTo(trafoPos2) <= trafoConnectionEpsilon
      );
    });
  };

  for (const trafo of trafos) {
    const anzahlLichtleisten = trafo.tree?.anzahlLichtleisten ?? 0;
    const anzahlLichtkappen = trafo.tree?.anzahlLichtkappen ?? 0;
    const lichtleistung =
      anzahlLichtleisten * APP.settings.licht.LichtleisteVerbrauch +
      anzahlLichtkappen * APP.settings.licht.LichtkappeVerbrauch;
    if (lichtleistung == 0) {
      yield {
        box: getBoxWorld(trafo),
        warnung: I18N("dieser Trafo wird nicht verwendet"),
        jacos: [trafo],
      };
    }
    if (lichtleistung > 0 && !isTrafoMitKabelVerbunden(trafo)) {
      yield {
        box: getBoxWorld(trafo),
        warnung: I18N("dieser Trafo ist nicht mit einem Stromkabel verbunden"),
        jacos: [trafo],
      };
    }
    if (lichtleistung > getMaxTrafoTyp().Leistung) {
      yield {
        box: getBoxWorld(trafo),
        warnung: I18N(
          "dieser Trafo ist an zuviele Verbraucher angeschlossen (%s Lichtleisten und %s Lichtkappen)"
        ),
        jacos: [trafo],
      };
    }
  }

  for (const kabel of arrangement.kabel.filter((kabel) => kabel.länge > MAX_KABELLÄNGE)) {
    yield {
      box: getBoxWorld(kabel),
      warnung: I18N("maximale Kabellänge überschritten", I18N.formatFloat(kabel.länge)),
      jacos: [kabel],
    };
  }

  // ermittle unverbundene Lichtleisten
  const lichtleistenElemente = arrangement.getElemente().filter((e) => e.hasLichtleisten);

  const point2BoundingBox = (p: Vector2, d: number) => ({
    minX: p.x - d,
    minY: p.y - d,
    maxX: p.x + d,
    maxY: p.y + d,
  });
  const anschlussSpatialIndex = new rbush<{ point: Vector2 }>();
  const endpunkte = arrangement.kabel.flatMap((kabel) =>
    kabel.lichtAnschlüsse.map((v) => Maths3.v3To2xz(v))
  );
  endpunkte.forEach((point) => {
    anschlussSpatialIndex.insert({ ...point2BoundingBox(point, epsilon), point });
  });
  const isPfostenverbunden = (pfosten) => {
    if (pfosten == null) return false;
    const pfostenPosition = Maths3.v3To2xz(pfosten.getWorldPosition());
    const queryResult = anschlussSpatialIndex.search(point2BoundingBox(pfostenPosition, epsilon));
    return queryResult.length > 0;
  };
  const unverbundeneLichtleisten = lichtleistenElemente.filter(
    (element) => !element.getPfosten().some(isPfostenverbunden)
  );
  for (const element of unverbundeneLichtleisten) {
    yield {
      box: getBoxWorld(element),
      warnung: I18N("unverbundene Lichtleiste"),
      jacos: [element],
    };
  }

  // ermittle unverbundene Lichtkappen
  const lichtkappenPfosten = arrangement.getPfosten().filter((e) => e.hasLichtkappe);
  const unverbundeneLichtkappen = lichtkappenPfosten.filter((l) => !isPfostenverbunden(l));
  for (const pfosten of unverbundeneLichtkappen) {
    yield {
      box: getBoxWorld(pfosten),
      warnung: I18N("unverbundene Lichtkappe"),
      jacos: [pfosten],
    };
  }

  for (const { pfosten, count } of getKabelProPfosten(
    lichtleistenElemente,
    lichtkappenPfosten,
    new Set<PointId>(endpunkte.map(getPointId))
  )) {
    let warnung = null;
    if (
      pfosten.isTorpfosten &&
      count >
        [pfosten.nachbarLinks, pfosten.nachbarRechts].filter((n) => (n as any)?.isUMProfil).length
    ) {
      warnung = I18N(
        "an einem Torpfosten können Kabel nur außen entlang verlegt werden (es wären %s)",
        count
      );
    } else if (pfosten.isUMProfil && count > 2) {
      warnung = I18N(
        "in einem UMProfil kann maximal ein Kabel verlegt werden (es wären %s)",
        count
      );
    } else if (count > 3) {
      warnung = I18N("in einem Pfosten können maximal 2 Kabel verlegt werden (es wären %s)", count);
    }
    if (warnung) {
      yield { box: getBoxWorld(pfosten), warnung, jacos: [pfosten] };
    }
  }

  const kabelSpatialIndex = getSpatialIndex(
    arrangement.kabel.flatMap((kabel) => {
      return Commons.pairwise(kabel.pfad.map((p) => Maths3.v3To2xz(p)));
    })
  );
  for (const kabel of arrangement.kabel) {
    outer: for (const p3 of kabel.lichtAnschlüsse) {
      const p = Maths3.v3To2xz(p3);
      const d = 0.02;
      const queryResult = kabelSpatialIndex.search(point2BoundingBox(p, d));
      for (const { polygon } of queryResult) {
        if (Maths2.distanceToLineSegment(p, polygon) < d) {
          continue outer;
        }
      }
      yield {
        box: new Box3().setFromCenterAndSize(p3, v3(0.08, 0.08, 0.08)),
        warnung: I18N("interner Fehler: Kabel erreicht Anschluss nicht"),
        jacos: [kabel],
      };
    }
  }
}

/**
 * das Folgende ist ein Algorithmus zum Ermitteln der Kabel pro Pfosten
 * ein Element mit Lichtleiste kann entweder am linken oder rechten Pfosten angeschlossen sein
 * die Idee: Gehe alle Pfosten durch, die ans Netz angeschlossen sind, und zähle die
 * Lichtleisten an jedem Pfosten.
 * Anschließend gehe von allen Pfosten mit minimaler Anzahl Lichtleisten aus.
 * Das sollte dann nur 1 Lichtleiste sein beim minimalen Pfosten. Damit ist ja klar, dass
 * die entsprechende Lichtleiste dort angeschlossen ist, und man kann beim Nachbarpfosten die
 * Anzahl der Lichtleisten um 1 reduzieren.
 * Wiederhole das für alle Pfosten.
 */
function getKabelProPfosten(
  lichtleistenElemente: ZElement[],
  lichtkappenPfosten: Pfosten[],
  endpunkte: Set<PointId>
) {
  interface PfostenNode {
    pfosten: Pfosten;
    adjazenz: Pfosten[];
    count: number;
  }
  const pointId3d = (p: Vector3) => getPointId(Maths3.v3To2xz(p));
  const pfostenNodeMap = new Map<Pfosten, PfostenNode>();
  lichtleistenElemente.forEach((element) => {
    const anliegendePfosten = element
      .getPfosten()
      .filter((pfosten) => pfosten != null && endpunkte.has(pointId3d(pfosten.getWorldPosition())));
    anliegendePfosten.forEach((pfosten) => {
      let node = pfostenNodeMap.get(pfosten);
      if (node == null) {
        node = { pfosten, adjazenz: [], count: 0 };
        pfostenNodeMap.set(pfosten, node);
      }
      node.count++;
      node.adjazenz.push(...anliegendePfosten.filter((p) => p !== pfosten));
    });
  });
  const pfostenHeap = createHeap<PfostenNode>();
  for (const node of pfostenNodeMap.values()) {
    pfostenHeap.add(node.count, node);
  }
  let node: PfostenNode;
  const kabelBag = new Bag<Pfosten>();
  const result: { pfosten: Pfosten; count: number }[] = [];
  while ((node = pfostenHeap.deleteMin())) {
    let { pfosten, adjazenz, count } = node;
    if (count == 0) {
      continue;
    }
    kabelBag.inc(pfosten, count);
    const nachbar = _.maxBy(adjazenz, (p) => pfostenNodeMap.get(p).count);
    if (nachbar) {
      const nachbarNode = pfostenNodeMap.get(nachbar);
      if (nachbarNode.count > 0) {
        nachbarNode.count--;
        pfostenHeap.setOrDecrease(nachbarNode.count, nachbarNode);
      }
    }
  }
  for (const pfosten of lichtkappenPfosten) {
    kabelBag.inc(pfosten, 1);
  }
  return result;
}
export const startPositionOfTree = (tree: Tree): Vector2 =>
  tree.root.children[0]?.path?.[0]?.clone();

export function flattenTreeNode(node: TreeNode, nodes = []): TreeNode[] {
  nodes.push(node);
  node.children.forEach((c) => flattenTreeNode(c, nodes));
  return nodes;
}

export function flattenTree(tree: Tree): TreeNode[] {
  return flattenTreeNode(tree.root).filter((node) => node.path.length > 0);
}

// zum Debuggen
export interface VTreeNode extends TreeNode {
  value: string;
  children: VTreeNode[];
}

// zum Debuggen
// ein String wie "a(b(de)c)" wird in eine TreeNode-Struktur umgewandelt:
//       a
//     /  \
//    b    c
//   /\
//  d  e
export function stringToTreeNode(input: string): VTreeNode {
  let idCounter = 0;
  function createNode(value: string): VTreeNode {
    return {
      id: idCounter++,
      value,
      children: [],
      path: [],
      distance: 0,
      lichtleiste: false,
      lichtkappe: false,
    };
  }
  const parents: VTreeNode[] = [];
  let root = null;
  let last: VTreeNode | null = null;
  for (let i = 0; i < input.length; i++) {
    const char = input[i];
    if (char === "(") {
      parents.push(last);
    } else if (char === ")") {
      parents.pop();
    } else {
      last = createNode(char);
      if (root == null) {
        root = last;
      } else {
        parents.at(-1)?.children.push(last);
      }
    }
  }
  return root;
}

// zum Debuggen
export function treeNodeToString(node: any): string {
  function traverse(node: VTreeNode): string {
    if (node.children.length == 0) {
      return node.value;
    }
    const childrenString = node.children.map(traverse).join("");
    return `${node.value}(${childrenString})`;
  }
  return traverse(node);
}

// trennt childToSplit aus einem Baum heraus
// a(b(de)c): splitTreeImmutable(a, b) => [a(bc),b(de)]
export function splitChildFromTreeImmutable(root: TreeNode, childToSplit: TreeNode): TreeNode[] {
  let childRoot = null;
  const visit = (node: TreeNode): TreeNode => {
    const children = node.children.map(visit);
    const i = node.children.findIndex((c) => c === childToSplit);
    if (i !== -1) {
      childRoot = {
        ...children[i],
        path: children[i].path.slice(-1),
        distance: 0,
      };
      children[i] = {
        ...children[i],
        children: [],
      };
    }
    return {
      ...node,
      children,
    };
  };
  const rootClone = visit(root);
  return [rootClone, childRoot];
}

// gibt das erste Kind nach Tiefensuche zurück
// a(b(de)c): findFirstChild(a) => d
export function findFirstLeafe(node: TreeNode): TreeNode {
  return node.children.length == 0 ? node : findFirstLeafe(node.children[0]);
}

// findet einen Pfad vom Child zur Wurzel
// a(b(de)c): findPathFromChildToRoot(e) => [a,b,e]
export function findPathFromRootToChild(node: TreeNode, child: TreeNode): TreeNode[] {
  if (node === child) {
    return [child];
  }
  for (const c of node.children) {
    const result = findPathFromRootToChild(c, child);
    if (result) {
      return [node, ...result];
    }
  }
  return null;
}

const cloneTree = (node: TreeNode) => ({ ...node, children: node.children.map(cloneTree) });

// sortiert einen Baum um - macht ein Kind zur neuen Wurzel
// a(b(ef)cd): makeFirstLeafNewRootImmutable(a) => f(b(ea(cd)))
export function makeFirstLeafNewRootImmutable(root: TreeNode): TreeNode {
  const cloneRoot = cloneTree(root);
  const firstChild = findFirstLeafe(cloneRoot);
  const path = findPathFromRootToChild(cloneRoot, firstChild);
  Commons.pairwise(path).map(([prev, next]) => {
    if (next.path.length == 1) {
      throw new Error("next.path.length == 1");
    } else {
      prev.path = next.path.toReversed();
      prev.distance = next.distance;
    }
    next.children = [...next.children, prev];
    prev.children = prev.children.filter((c) => c !== next);
  });
  firstChild.distance = 0;
  firstChild.path = firstChild.path.slice(-1);
  return firstChild;
}

/**
 * verlegt Kabel linear entlang des Baumes
 * @param tree
 * @param y die Tiefe, in der das Kabel verlegt wird
 * @param dy der Durchmesser des Kabels bzw. um wie viel tiefer wird das Kabel verlegt, wenn es
 *           denselben Weg zurück läuft
 */
export function verlegeKabelEntlangDesBaumes(
  tree: Tree,
  y: number,
  dy: number
): { liste: Kabel[]; y: number } {
  // Legt ein oder mehrere Kabel durch einen Baum. Die Wurzel entspricht dem Trafo, jeder weitere
  // Knoten entspricht einer Lichtleiste.
  // Ein Kabel ist ein Bus-System, an dem mehrere Lichtleisten angeschlossen werden.
  // Beispiel:
  //
  //         1 = Trafo
  //         |
  //     3---2---5---6---7
  //         |
  //         4
  //
  // In diesem Beispiel haben wir einen Baum mit 7 Anschlusspunkten, wobei noch die nicht
  // dargestellten Gewichtungen der Einzelstrecken berücksichtigen werden müssen.
  // Dazu ein paar Lösungsbeispiele:
  // a) 1->2, 3->2->5->6->7, 2->4
  // a) 1->2, 3->2->4->2->5->6->7
  // b) 1->2->5->6->7->6->5->2->3, 2->4 (2 Kabel, weil das erste Kabel zu lange wurde)
  // c) 1->2->3->2->4->2->5->6->7
  //
  // Regeln:
  // Die Anzahl und Lönge der Kabel soll minimal sein.
  // Es gibt eine maximale Kabellänge.
  // Am Trafo muss stets ein Basiskabel angeschlossen sein.
  // In jedem Knoten gibt es folgende Optionen:
  //    1) Anzahl Kindknoten <= 1 : Lege das Kabel weiter
  //    2) Anzahl Kindknoten >= 2, pro Kindknoten :
  //       a) Lege das Kabel in einer Schlaufe um den Kindknoten
  //       b) Füge ein weiteres Kabel als Abzweigung hinzu
  //    3) Anzahl Kindknoten == 0 :
  //       a) Nimm den Knoten als Start- oder Endknoten des Kabels
  //       b) Lege das Kabel in einer Schlaufe zurück
  //    *) Unabhängig von den Kindknoten gilt:
  //       Falls die maximale Kabellänge zwischen zwei Knoten überschritten wird,
  //       wird ein weiteres Kabel angeschlossen.
  //
  // Kombinatorische Explosion:
  // Sein n die Anzahl der Knoten mit 0 oder mehr als 2 Kindern, dann gibt es 2^n Möglichkeiten,
  // das Kabel zu verlegen. Das ist ein vermutlich np-vollständiges Optimierungsproblem.
  //
  // Lösung:
  // Mittels eigenem Greedy-Verfahren.
  // Endpunkte haben 0 Kinder, Kreuzungspunkte >= 2 Kinder.
  // Die kürzeste Verkabellung in einem Baum ist durch folgenden Algorithmus bestimmt:
  // Für die Menge der Knoten (Terminal oder Kreuzungspunkt) und einem Startknoten,
  // iteriere über die adjazenten Kanten und nimm die minimale noch nicht besuchte Kante, sonst
  // die minimale Kante, und füge sie zum Weg hinzu. Wiederhole, bis alle Kanten besucht sind.
  // Die entstandene Verkabellung ist vollständig und minimal für ein durchgehendes Kabel,
  // die Laufzeit ist O(n) bei n Kanten.
  //
  // Bei einem Split wird das Kabel in zwei neue aufgeteilt.
  // Es gibt zwei Arten von Splits:
  // 1) Kreuzungsplit: Ein Split an einem Kreuzungspunkt.
  //    An einem Kreuzungspunkt gibt es einen möglichen Split pro Kante (parent und pro child).
  // 2) Streckensplit: Ein Split innerhalb der Strecke zwischen zwei Punkten
  //
  // Gesamtalgorithmus:
  // 1) Erzeuge die kürzeste Verkabellung.
  // 2) Finde pro Kabel, solange es die maximale Länge überschreitet und solange es darin
  // Kreuzungspunkte gibt, den Split, der
  // a) beide Kabellängen valide macht oder
  // b) eine Kabellänge valide macht oder
  // c) die maximale Kabellänge entfallen lässt
  // Iteriere dazu linear über alle möglichen Splits und führe diese testweise aus.
  // Wiederhole dann 2) für beide Teilkabel.
  // Bei jedem Split wird der Verkabellungsalgorithmus neu angewendet für die Teilbäume.
  //
  // Für das Verständnis des Algorithmus ist noch wichtig, dass zwischen der Darstellung als Baum
  // und als Kabel hin- und her-gemappt wird, weil jede Darstellung sich für bestimmte Operationen
  // besser eignet:
  // * Aus dem Baum kann schnell eine optimale Verkabellung abgeleitet werden
  // * Aus dem Baum können direkt Kreuzungspunkte ermittelt werden
  // * Aus dem Kabel kann direkt die Länge und konkreter Pfad ermittelt werden
  // * Aus dem Kabel wird ein Baum erstellt, damit die Kreuzungspunkte des Baumes
  //   ermittelt und gesplittet werden können, um aus den Teilbäumen Kabel abzuleiten
  //
  // Die Gesamtlaufzeit ist wohl O(n²) im Worst Case, vermutlich in der Praxis eher O(n log n)

  enum Richtung {
    vorwärts = "vorwärts",
    rückwärts = "rückwärts",
  }
  const { vorwärts, rückwärts } = Richtung;
  enum Anschluss {
    Trafo = "Trafo",
    Bus = "Bus",
  }
  const { Trafo, Bus } = Anschluss;

  interface Pfad {
    // In der Regel startet und endet der path in einem TreeNode. Hier sind alle beteiligten
    // TreeNodes enthalten, 1 oder 2 Stück
    nodes: TreeNode[];
    path: Vector2[];
    distance: number;
    incY: boolean;
  }

  interface PKabel {
    länge: number;
    pfade: Pfad[];
    startAnschluss: "Trafo" | "Bus" | null;
    endAnschluss: "Bus" | null;
    richtungZuletzt: Richtung;
  }

  function createKabel(): PKabel {
    return {
      länge: 0,
      pfade: [],
      startAnschluss: null,
      endAnschluss: null,
      richtungZuletzt: vorwärts,
    };
  }

  function recursiveDistance(n: TreeNode) {
    return n.distance + n.children.reduce((sum, x) => sum + recursiveDistance(x), 0);
  }

  function sortNodes(nodeList: TreeNode[]) {
    const distanceMap = new Map<TreeNode, number>();
    nodeList.forEach((n) => distanceMap.set(n, recursiveDistance(n)));
    nodeList.forEach((node) =>
      node.children.sort((a, b) => distanceMap.get(a) - distanceMap.get(b))
    );
  }

  function verlege(kabel: PKabel, startNode: TreeNode, endNode: TreeNode, richtung: Richtung) {
    const path = richtung == vorwärts ? endNode.path : endNode.path.toReversed();
    const nodes = richtung == vorwärts ? [startNode, endNode] : [endNode, startNode];
    kabel.pfade.push({
      path,
      nodes,
      distance: endNode.distance,
      incY: kabel.richtungZuletzt !== richtung && richtung === rückwärts,
    });
    kabel.länge += endNode.distance;
    kabel.richtungZuletzt = richtung;
  }

  function erzeugeKürzesteVerlegungMitEinemEinzigenKabel(start: TreeNode, ausgabeKabel: PKabel) {
    // greedy Algorithmus
    const nodeList = flattenTreeNode(start);
    const nodeCount = nodeList.length;
    sortNodes(nodeList);
    const visited = new Set<TreeNode>();
    createShortestWiringIntern(start);
    function createShortestWiringIntern(node: TreeNode) {
      visited.add(node);
      for (const child of node.children) {
        verlege(ausgabeKabel, node, child, vorwärts);
        createShortestWiringIntern(child);
        if (visited.size < nodeCount) {
          verlege(ausgabeKabel, node, child, rückwärts);
        }
      }
    }
  }

  // Umkehrfunktion zu erzeugeKürzesteVerlegungMitEinemEinzigenKabel
  function kabelZuBaum(kabel: PKabel): TreeNode {
    const nodeMap = new Map<TreeNode, TreeNode>();
    const getNode = (n: TreeNode) => {
      let node = nodeMap.get(n);
      if (node == null) {
        node = { ...n, children: [] };
        nodeMap.set(n, node);
      }
      return node;
    };
    for (const pfad of kabel.pfade) {
      const n = pfad.nodes.map(getNode);
      if (n.length > 1 && !n[1].children.includes(n[0]) && !n[0].children.includes(n[1])) {
        n[0].children.push(n[1]);
      }
    }
    return [...nodeMap.values()][0];
  }

  function splitPfade(pfad: Pfad[], länge: number, epsilon: number): [Pfad[], Pfad[]] {
    let s = 0;
    for (let i = 0; i < pfad.length; i++) {
      const p = pfad[i];
      const d = p.distance;
      const diff = s + d - länge;
      if (Math.abs(diff) < epsilon) {
        const pfad0 = pfad.slice(0, i);
        const pfad1 = pfad.slice(i);
        return [pfad0, pfad1];
      } else if (diff > 0) {
        const pfad0 = pfad.slice(0, i);
        const pfad3 = pfad.slice(i + 1);
        const [p1, p2] = Maths2.splitPath(p.path, d - diff, epsilon);
        const pfad1: Pfad = {
          nodes: [p.nodes[0]],
          path: p1,
          distance: länge - s,
          incY: false,
        };
        const pfad2: Pfad = {
          nodes: [p.nodes[1]],
          path: p2,
          distance: diff,
          incY: false,
        };
        const a0 = [...pfad0, pfad1];
        const a1 = [pfad2, ...pfad3];
        return [a0, a1];
      }
      s += d;
    }
    throw new Error("splitPfade splittet nicht");
  }

  function getKreuzungspunkte(tree: TreeNode) {
    return flattenTreeNode(tree).filter((n, i) => i > 0 && n.children.length > 1);
  }

  function getKreuzungsSplits(kabel: PKabel): [PKabel, PKabel][] {
    const tree = kabelZuBaum(kabel);
    const kreuzpunkte = getKreuzungspunkte(tree);
    const result: [PKabel, PKabel][] = [];
    const pushAsKabelSplit = (a: TreeNode, b: TreeNode) => {
      const kables = [a, b].map((treeNode) => {
        const kabel = createKabel();
        erzeugeKürzesteVerlegungMitEinemEinzigenKabel(treeNode, kabel);
        return kabel;
      }) as [PKabel, PKabel];
      result.push(kables);
      return kables;
    };
    for (const kreuzungspunkt of kreuzpunkte) {
      const [treeClone, kreuzungspunktTree] = splitChildFromTreeImmutable(tree, kreuzungspunkt);
      const childRoot = makeFirstLeafNewRootImmutable(kreuzungspunktTree);
      const [treeKabel, childKabel] = pushAsKabelSplit(treeClone, childRoot);
      treeKabel.startAnschluss = kabel.startAnschluss;
      // fixme es ist nicht garantiert, dass endAnschluss im treeKabel.endAnschluss endet - es könnte auch eine Schlaufe sein!
      // falls es eine Schlaufe ist, muss diese durch einen weiteren Split aufgelöst werden
      treeKabel.endAnschluss = Bus;
      for (const child of kreuzungspunkt.children) {
        const [treeClone, childClone] = splitChildFromTreeImmutable(tree, child);
        const [treeKabel, childKabel] = pushAsKabelSplit(treeClone, childClone);
        treeKabel.startAnschluss = kabel.startAnschluss;
        // fixme es ist nicht garantiert, dass childKabel.startAnschluss im treeKabel endet - es könnte auch eine Schlaufe sein!
        // gleiche Lösung wie beim vorigen Fixme
        childKabel.startAnschluss = Bus;
      }
    }
    return result;
  }

  function splitKabelstrecke(kabel: PKabel, länge: number): [PKabel, PKabel] {
    if (kabel.länge <= länge) {
      throw new Error("splitKabelstrecke Kabellänge > Länge");
    }

    const kabel1 = {
      ...kabel,
    };
    const kabel2 = createKabel();
    [kabel1.pfade, kabel2.pfade] = splitPfade(kabel.pfade, länge, 0.01);
    kabel1.endAnschluss = Bus;
    kabel1.länge = länge;
    kabel2.länge = kabel.länge - länge;
    kabel2.endAnschluss = kabel.endAnschluss;
    kabel2.startAnschluss = null;
    // console.log("splitKabelstrecke", { kabel1, kabel2 });
    return [kabel1, kabel2];
  }

  function getStreckenSplits(kabel: PKabel): [PKabel, PKabel][] {
    if (kabel.länge <= MAX_KABELLÄNGE) {
      return [];
    }
    return [splitKabelstrecke(kabel, MAX_KABELLÄNGE)];
  }

  // wandle das Ergebnis (PKabel) in die Zieldatenstruktur (Kabel) um
  function pkabelZuKabel(pkabels: PKabel[]): Kabel[] {
    const lichtAnschlüsseSet = new Set<TreeNode>();
    const liste: Kabel[] = pkabels.map((pkabel: PKabel) => {
      const lichtAnschlüsse: Vector3[] = [];
      const pfad: Vector3[] = [];
      let index = -1;
      for (const p of pkabel.pfade) {
        index++;
        if (p.incY) {
          y += dy;
        }
        let anschlussNodes = p.nodes;
        anschlussNodes = anschlussNodes.filter((n) => !lichtAnschlüsseSet.has(n));
        anschlussNodes.forEach((n) => lichtAnschlüsseSet.add(n));
        const anschlüsse = anschlussNodes
          .filter((n) => n?.lichtkappe || n?.lichtleiste)
          .map((node) => Maths3.v2To3xz(node.path.at(-1), y));
        lichtAnschlüsse.push(...anschlüsse);
        const pp = p.path.map((v) => Maths3.v2To3xz(v, y));
        pfad.push(...pp);
      }
      y += dy / 9;
      return {
        länge: pkabel.länge,
        pfad,
        lichtAnschlüsse: lichtAnschlüsse,
        startAnschluss: pkabel.startAnschluss,
        endAnschluss: pkabel.endAnschluss,
        schlaufen: pkabel.pfade.filter((p) => p.incY).length,
      };
    });
    return liste;
  }

  const validationEpsilon = 0.01;

  function valid(kabel: PKabel, existingCables: Set<PKabel>): boolean {
    if (kabel.länge > MAX_KABELLÄNGE + validationEpsilon) {
      return false;
    }
    if (kabel.startAnschluss == "Trafo") {
      return true;
    }
    const atNode = (kabel: PKabel, i: number) => kabel.pfade.at(i)?.nodes?.at(i);
    const connects = (nodes: Set<TreeNode>) => (kabel: PKabel) =>
      [0, -1].some((i) => nodes.has(atNode(kabel, i)));
    const otherNodes = new Set<TreeNode>();
    for (const k of existingCables) {
      if (k !== kabel) {
        for (const pfad of k.pfade) {
          for (const node of pfad.nodes) {
            otherNodes.add(node);
          }
        }
      }
    }
    if (connects(otherNodes)(kabel)) {
      return true;
    }
    const myNodes = new Set<TreeNode>();
    for (const pfad of kabel.pfade) {
      for (const node of pfad.nodes) {
        myNodes.add(node);
      }
    }
    if (existingCables.some(connects(myNodes))) {
      return true;
    }
    return false;
  }

  interface TrafoBusSplitKandidat {
    child: TreeNode;
    point: Vector2;
    distanceToTrafo: number;
    distanceAlongChildPath: number;
  }

  function getClosestPointOnSegment(p: Vector2, a: Vector2, b: Vector2) {
    const ab = b.clone().sub(a);
    const lenSq = ab.lengthSq();
    if (lenSq <= 0) {
      return { point: a.clone(), t: 0, distance: p.distanceTo(a) };
    }
    const t = Maths.clamp(p.clone().sub(a).dot(ab) / lenSq, 0, 1);
    const point = a.clone().add(ab.multiplyScalar(t));
    return { point, t, distance: p.distanceTo(point) };
  }

  function findeTrafoBusSplitKandidat(
    root: TreeNode,
    epsilon: number
  ): TrafoBusSplitKandidat | null {
    const trafoPosition = root.children[0]?.path?.[0];
    if (trafoPosition == null) {
      return null;
    }

    const countLichter = (node: TreeNode): number =>
      flattenTreeNode(node).filter((n) => n.lichtkappe || n.lichtleiste).length;
    const totalLichtCount = countLichter(root);

    const kandidatIstValide = (kandidat: TrafoBusSplitKandidat): boolean => {
      const split = splitTreeAmTrafoBusKandidaten(root, kandidat, epsilon);
      if (split == null) {
        return false;
      }
      const busRootWithTrafo = makeKnotenZurRootImmutable(split.rootWithSplit, split.splitNode);
      const busRoot = entferneTrafoBlattVomBusRoot(busRootWithTrafo, trafoPosition);
      return countLichter(busRoot) === totalLichtCount;
    };

    let best: TrafoBusSplitKandidat | null = null;

    const visit = (node: TreeNode) => {
      for (const child of node.children) {
        const path = child.path;
        let prefixDistance = 0;
        for (let i = 0; i < path.length - 1; i++) {
          const a = path[i];
          const b = path[i + 1];
          const segLen = a.distanceTo(b);
          const { point, t, distance } = getClosestPointOnSegment(trafoPosition, a, b);
          // Nur echte Linienzug-Splits im Segmentinneren berücksichtigen.
          const isInterior = t > epsilon && t < 1 - epsilon;
          if (
            isInterior &&
            distance < TRAFO_KABELLÄNGE + epsilon &&
            kandidatIstValide({
              child,
              point,
              distanceToTrafo: distance,
              distanceAlongChildPath: prefixDistance + a.distanceTo(point),
            }) &&
            (best == null || distance < best.distanceToTrafo)
          ) {
            best = {
              child,
              point,
              distanceToTrafo: distance,
              distanceAlongChildPath: prefixDistance + a.distanceTo(point),
            };
          }
          prefixDistance += segLen;
        }
        visit(child);
      }
    };

    visit(root);
    return best;
  }

  function findParentNode(root: TreeNode, child: TreeNode): TreeNode | null {
    for (const c of root.children) {
      if (c === child) {
        return root;
      }
      const nested = findParentNode(c, child);
      if (nested != null) {
        return nested;
      }
    }
    return null;
  }

  function splitTreeAmTrafoBusKandidaten(
    root: TreeNode,
    kandidat: TrafoBusSplitKandidat,
    epsilon: number
  ): { rootWithSplit: TreeNode; splitNode: TreeNode } | null {
    const cloneMap = new Map<TreeNode, TreeNode>();
    const cloneWithMap = (node: TreeNode): TreeNode => {
      const clone = { ...node, children: node.children.map(cloneWithMap) };
      cloneMap.set(node, clone);
      return clone;
    };

    const rootClone = cloneWithMap(root);
    const childClone = cloneMap.get(kandidat.child);
    if (childClone == null) {
      return null;
    }
    const parentClone = findParentNode(rootClone, childClone);
    if (parentClone == null) {
      return null;
    }

    const [pathA, pathB] = Maths2.splitPath(
      childClone.path,
      kandidat.distanceAlongChildPath,
      epsilon
    );
    if (pathA.length < 2 || pathB.length < 2) {
      return null;
    }

    const nextId =
      _.max(
        flattenTreeNode(rootClone)
          .map((n) => n.id)
          .filter((id) => id != null)
      ) + 1;
    const splitNode: TreeNode = {
      id: nextId,
      children: [childClone],
      path: pathA,
      distance: Maths2.sumPathDistance(pathA),
      lichtkappe: false,
      lichtleiste: false,
    };
    childClone.path = pathB;
    childClone.distance = Maths2.sumPathDistance(pathB);

    const i = parentClone.children.findIndex((c) => c === childClone);
    if (i < 0) {
      return null;
    }
    parentClone.children[i] = splitNode;

    return { rootWithSplit: rootClone, splitNode };
  }

  function makeKnotenZurRootImmutable(root: TreeNode, child: TreeNode): TreeNode {
    if (root === child) {
      return root;
    }
    const path = findPathFromRootToChild(root, child);
    if (path == null || path.length < 2) {
      return root;
    }
    Commons.pairwise(path).forEach(([prev, next]) => {
      if (next.path.length == 1) {
        throw new Error("next.path.length == 1");
      }
      prev.path = next.path.toReversed();
      prev.distance = next.distance;
      next.children = [...next.children, prev];
      prev.children = prev.children.filter((c) => c !== next);
    });
    child.distance = 0;
    child.path = child.path.slice(-1);
    return child;
  }

  /**
   * Entfernt den alten Root-Blattknoten (Trafo-Position) aus dem re-rooted Baum.
   * Nach dem Re-Rooting wird der alte Root zu einem Blatt ohne Licht-Flags.
   * Wir dürfen nicht den ganzen Subtree entfernen, weil dieser auch echte Lichtknoten enthalten kann.
   */
  function entferneTrafoBlattVomBusRoot(root: TreeNode, trafoPosition: Vector2): TreeNode {
    const trafoPid = getPointId(trafoPosition);
    const isTrafoLeaf = (node: TreeNode): boolean =>
      node.children.length === 0 &&
      !node.lichtkappe &&
      !node.lichtleiste &&
      node.path.some((p) => getPointId(p) === trafoPid);

    function removeLeafRecursive(node: TreeNode): boolean {
      for (let i = node.children.length - 1; i >= 0; i--) {
        const child = node.children[i];
        if (isTrafoLeaf(child)) {
          node.children.splice(i, 1);
          return true;
        }
        if (removeLeafRecursive(child)) {
          return true;
        }
      }
      return false;
    }

    removeLeafRecursive(root);
    return root;
  }

  /**
   * Strategie 1: Der Trafo liegt nahe an einem vorhandenen Pfad – erzeuge ein kurzes Trafo-Kabel
   * zum nächsten Punkt auf dem Bus und verlege den Bus separat.
   */
  function verlegeMitTrafoBusSplit(
    root: TreeNode,
    kabels: Set<PKabel>,
    startKabel: PKabel,
    kandidat: TrafoBusSplitKandidat,
    split: { rootWithSplit: TreeNode; splitNode: TreeNode }
  ) {
    const trafoPosition = root.children[0]?.path?.[0];
    const splitPosition = kandidat.point;
    // Bus am Splitpunkt neu verwurzeln und danach nur den Trafo-Ast entfernen.
    const busRootWithTrafo = makeKnotenZurRootImmutable(split.rootWithSplit, split.splitNode);
    const busRootCleaned = entferneTrafoBlattVomBusRoot(busRootWithTrafo, trafoPosition);
    // Den Bus-Baum an einem Blatt neu verwurzeln, damit er linear durchlaufen wird (keine Schlaufe).
    const busRoot = makeFirstLeafNewRootImmutable(busRootCleaned);
    erzeugeKürzesteVerlegungMitEinemEinzigenKabel(busRoot, startKabel);
    startKabel.startAnschluss = null;
    startKabel.endAnschluss = null;

    const trafoKabel = createKabel();
    kabels.add(trafoKabel);
    const distTrafoZuBus = trafoPosition.distanceTo(splitPosition);
    trafoKabel.länge = distTrafoZuBus;
    trafoKabel.pfade = [
      {
        nodes: [],
        path: [trafoPosition.clone(), splitPosition.clone()],
        distance: distTrafoZuBus,
        incY: false,
      },
    ];
    trafoKabel.startAnschluss = Trafo;
    trafoKabel.endAnschluss = Bus;
  }

  /**
   * Strategie 2: Fallback – der Trafo ist sehr nah am ersten Kreuzungspunkt.
   * Splitte den Baum direkt an diesem Knoten.
   */
  function verlegeMitFallbackSplit(
    root: TreeNode,
    kabels: Set<PKabel>,
    startKabel: PKabel,
    kreuzungspunkt: TreeNode
  ) {
    const [trafoNode, kreuzungspunktTree] = splitChildFromTreeImmutable(root, kreuzungspunkt);
    const busRoot = makeFirstLeafNewRootImmutable(kreuzungspunktTree);
    erzeugeKürzesteVerlegungMitEinemEinzigenKabel(busRoot, startKabel);
    const trafoKabel = createKabel();
    kabels.add(trafoKabel);
    erzeugeKürzesteVerlegungMitEinemEinzigenKabel(trafoNode, trafoKabel);
    trafoKabel.startAnschluss = Trafo;
    trafoKabel.endAnschluss = Bus;
    startKabel.startAnschluss = null;
    startKabel.endAnschluss = null;
  }

  /**
   * Löst invalide Kabel (zu lang) iterativ durch Kreuzungs- und Streckensplits auf.
   */
  function löseInvalideKabelAuf(kabels: Set<PKabel>, invalidKabels: Set<PKabel>) {
    while (invalidKabels.size > 0) {
      if (invalidKabels.size > 100) {
        throw new Error("infinite loop");
      }
      const kabel = invalidKabels.first();
      invalidKabels.delete(kabel);
      const splits: [PKabel, PKabel][] = [
        ...getStreckenSplits(kabel),
        ...getKreuzungsSplits(kabel),
      ];
      if (splits.length > 0) {
        const ranking = (p: PKabel) => (valid(p, kabels) ? p.länge : p.länge + 1000);
        const split = _.minBy(splits, ([a, b]) => ranking(a) + ranking(b));
        kabels.delete(kabel);
        for (const a of split) {
          kabels.add(a);
          if (!valid(a, kabels)) {
            invalidKabels.add(a);
          }
        }
      }
    }
  }

  function validateRoot(root: TreeNode) {
    if (root.path.length != 0) {
      console.error("root must have a path of length 0", root);
      debugger;
    }
    for (const firstNode of root.children) {
      if (firstNode.path.length < 2) {
        console.error("firstNode must have a path length >= 2", root);
        debugger;
      }
      if (firstNode.distance < 0.00001) {
        console.error("firstNode must not have 0 distance", root);
        debugger;
      }
    }
  }

  // Hauptalgorithmus der Kabelverlegung innerhalb des minimalen Spannbaumes
  const root = tree.root;
  if (root == null) {
    return { liste: [], y };
  }
  validateRoot(root);

  const kabels = new Set<PKabel>();
  const invalidKabels = new Set<PKabel>();
  const startKabel = createKabel();
  kabels.add(startKabel);

  const firstNode = root.children[0];
  const trafoBusSplitKandidat = findeTrafoBusSplitKandidat(root, validationEpsilon);
  const trafoBusSplit =
    trafoBusSplitKandidat == null
      ? null
      : splitTreeAmTrafoBusKandidaten(root, trafoBusSplitKandidat, validationEpsilon);

  if (trafoBusSplit != null) {
    verlegeMitTrafoBusSplit(root, kabels, startKabel, trafoBusSplitKandidat, trafoBusSplit);
  } else if (
    root.children.length == 1 &&
    firstNode.distance < TRAFO_KABELLÄNGE + validationEpsilon &&
    firstNode.children.length > 1
  ) {
    verlegeMitFallbackSplit(root, kabels, startKabel, firstNode);
  } else {
    startKabel.startAnschluss = Trafo;
    erzeugeKürzesteVerlegungMitEinemEinzigenKabel(root, startKabel);
  }

  if (!valid(startKabel, kabels)) {
    invalidKabels.add(startKabel);
  }
  löseInvalideKabelAuf(kabels, invalidKabels);

  let liste = pkabelZuKabel([...kabels]);
  if (liste.some((l) => l.pfad.length == 0)) {
    console.error("Kabel ohne Pfad!", liste);
    debugger;
  }
  liste = liste.filter((l) => l.pfad.length > 0);
  return { liste, y };
}
